import { useCallback, memo } from "react";
import BuildingLineIcon from "remixicon-react/BuildingLineIcon";
import { Link, useLocation } from "react-router-dom";
import { useStyles } from "../styles";
import { CompanyType } from "$types";
import useJStyles from "$hooks/useJStyles";
import { ICompany } from "$services/Entreprises.service";

import { EditableInfoContainer } from "$components/InfoContainer/EditableInfoContainer";

export const CompanyInfoBlock = memo(function CompanyInfoBlock({
  company,
  isDisabled,
  tab,
  openPopin,
  isLoading,
  contributors,
  clients,
  carrier,
  logiWarehouse,
}: {
  company: ICompany;
  isDisabled: boolean;
  tab: string;
  openPopin: () => void;
  contributors: any;
  clients: any;
  carrier: any;
  logiWarehouse: any;
  isLoading: boolean;
}) {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const location = useLocation();

  const makeLink = useCallback(
    (linkedCompany: any, displayPipe: boolean) => {
      const existingBreadcrumbs = (
        location.state as
          { breadcrumbs?: Array<{ path: string; name: string }> } | undefined
      )?.breadcrumbs;
      const newBreadcrumbs = [
        ...(existingBreadcrumbs ?? [
          { path: "/entreprise", name: "Liste des entreprises" },
          { path: location.pathname, name: company?.name },
        ]),
        { path: `/entreprise/${linkedCompany._id}`, name: linkedCompany.name },
      ];
      return (
        <Link
          to={`/entreprise/${linkedCompany._id}`}
          state={{ breadcrumbs: newBreadcrumbs }}
          className={jclasses.ghostLink}
          key={linkedCompany._id}
        >
          {linkedCompany.name}
          {displayPipe && " | "}
        </Link>
      );
    },
    [company?.name, jclasses.ghostLink, location]
  );

  const data = [
    { label: "Nom", value: company?.name },
    { label: "SIRET", value: company?.siret || "N/A" },
    ...(contributors?.length &&
    company?.companyTypes?.filter((t) => t === tab).includes(CompanyType.CLIENT)
      ? [
          {
            label: "Intervenant(s)",
            value: contributors
              ?.filter(({ isActive }) => isActive === true)
              ?.map((c, idx) => makeLink(c, idx !== contributors.length - 1)),
          },
        ]
      : []),
    ...(company?.companyTypes
      ?.filter((t) => t === tab)
      .includes(CompanyType.CONTRIBUTOR) && clients.length
      ? [
          {
            label: "Client(s)",
            value: clients?.map((c, idx) =>
              makeLink(c, idx !== clients.length - 1)
            ),
          },
        ]
      : []),
    ...(company?.supplierOf && tab === CompanyType.SUPPLIER
      ? [
          {
            label: "Entreprise(s) rattachée(s)",
            value: company?.supplierOf.map((c, idx) =>
              makeLink(c, idx !== company.supplierOf.length - 1)
            ),
          },
        ]
      : []),

    ...(company?.companyTypes?.includes(CompanyType.AGENCY)
      ? [{ label: "E-mail", value: company.email || "N/A" }]
      : []),
    ...(company?.companyTypes?.includes(CompanyType.AGENCY) && carrier
      ? [{ label: "Transporteur", value: makeLink(carrier, false) }]
      : []),
    ...(company?.companyTypes?.includes(CompanyType.LOGISTICIAN) &&
    logiWarehouse.length
      ? [
          {
            label: "Entreprise(s) rattachée(s)",
            value: logiWarehouse.map((c, idx) =>
              makeLink(c, idx !== logiWarehouse.length - 1)
            ),
          },
        ]
      : []),
  ];

  const dataRight = [
    {
      label: "Adresse",
      value: (
        <>
          {company?.adress}
          <br />
          {company?.complementAdress && (
            <>
              {company?.complementAdress}
              <br />
            </>
          )}
          {company?.postalCode} {company?.city}
          <br />
          {company?.country}
        </>
      ),
    },
  ];

  return (
    <EditableInfoContainer
      title="Informations société"
      icon={<BuildingLineIcon />}
      editButton={{ onClick: openPopin, isDisabled }}
      style={{
        width: company?.companyTypes?.includes(CompanyType.AGENCY)
          ? "100%"
          : "",
      }}
      data={data}
      isLoading={isLoading}
      dataRight={dataRight}
      className={[classes.companyInfos]}
    />
  );
});
